import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import type { ScoToolSession } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** Sessions shown on the list page before the user clicks Load more. */
export const WIL_SESSION_LIST_PAGE_SIZE = 5;
export const WIL_SESSIONS_QUERY = gql`
  query AdvisorWilSessions($toolId: String, $userId: String, $order: [[String]], $page: Int, $pageSize: Int) {
    scoToolSessions(toolId: $toolId, userId: $userId, order: $order, page: $page, pageSize: $pageSize) {
      items {
        id
        toolSessionId
        title
        createdAt
        updatedAt
        threadId
        generatedSummary
        thread {
          id
          threadId
          lastActiveAt
          commentsCount
          comments(pageSize: 5, order: [["createdAt", "asc"]]) {
            id
            items {
              id
              body
              createdByUserId
              createdByUser {
                id
                userId
              }
            }
          }
        }
      }
      pagination {
        itemCount
        page
        pageSize
        hasNextPage
      }
    }
  }
`;
export type UseWilSessionsOptions = {
  toolId?: string;
  userId?: string;
  /**
   * When set (chat view), resets the list window so returning to the list starts at page 1.
   */
  activeToolSessionId?: string;
  pageSize?: number;
};

/**
 * Current user's Wil tool sessions list + client-side "Load more" window.
 * Always scopes by userId so admins see their own chats, not every user's.
 */
export function useWilSessions(options) {
  const $ = _c(35);
  const {
    toolId,
    userId,
    activeToolSessionId,
    pageSize: t0
  } = options;
  const pageSize = t0 === undefined ? 50 : t0;
  const [visibleSessionCount, setVisibleSessionCount] = useState(WIL_SESSION_LIST_PAGE_SIZE);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [["updatedAt", "desc"]];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pageSize || $[2] !== toolId || $[3] !== userId) {
    t2 = {
      toolId,
      userId,
      order: t1,
      page: 1,
      pageSize
    };
    $[1] = pageSize;
    $[2] = toolId;
    $[3] = userId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !toolId || !userId;
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      variables: t2,
      skip: t3,
      fetchPolicy: "cache-and-network"
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const sessionsQuery = useQuery(WIL_SESSIONS_QUERY, t4);
  sessionsQuery.data?.scoToolSessions?.items;
  let t5;
  if ($[8] !== sessionsQuery.data?.scoToolSessions?.items) {
    t5 = sessionsQuery.data?.scoToolSessions?.items || [];
    $[8] = sessionsQuery.data?.scoToolSessions?.items;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = t5.filter(Boolean);
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const firstPageSessions = t6 as ScoToolSession[];
  const t7 = sessionsQuery.data?.scoToolSessions?.pagination?.hasNextPage;
  const t8 = `${toolId || ""}:${userId || ""}`;
  let t9;
  if ($[12] !== firstPageSessions || $[13] !== sessionsQuery.fetchMore || $[14] !== t7 || $[15] !== t8) {
    t9 = {
      fetchMore: sessionsQuery.fetchMore,
      field: "scoToolSessions",
      firstPageItems: firstPageSessions,
      hasNextPage: t7,
      resetKey: t8
    };
    $[12] = firstPageSessions;
    $[13] = sessionsQuery.fetchMore;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    items: sessions,
    loadMore: fetchMoreSessions,
    loadingMore: loadingMoreSessions,
    hasNextPage: sessionsHasNext
  } = useAdvisorsCollectionLoadMore(t9);
  let t10;
  if ($[17] !== sessions || $[18] !== visibleSessionCount) {
    t10 = sessions.slice(0, visibleSessionCount);
    $[17] = sessions;
    $[18] = visibleSessionCount;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const visibleSessions = t10;
  const hasMoreSessions = sessions.length > visibleSessionCount || sessionsHasNext;
  let t11;
  let t12;
  if ($[20] !== activeToolSessionId) {
    t11 = () => {
      if (activeToolSessionId) {
        setVisibleSessionCount(WIL_SESSION_LIST_PAGE_SIZE);
      }
    };
    t12 = [activeToolSessionId];
    $[20] = activeToolSessionId;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  if ($[23] !== fetchMoreSessions || $[24] !== loadingMoreSessions || $[25] !== sessions.length || $[26] !== sessionsHasNext || $[27] !== visibleSessionCount) {
    t13 = () => {
      const nextCount = visibleSessionCount + WIL_SESSION_LIST_PAGE_SIZE;
      setVisibleSessionCount(nextCount);
      if (nextCount > sessions.length && sessionsHasNext && !loadingMoreSessions) {
        fetchMoreSessions();
      }
    };
    $[23] = fetchMoreSessions;
    $[24] = loadingMoreSessions;
    $[25] = sessions.length;
    $[26] = sessionsHasNext;
    $[27] = visibleSessionCount;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const loadMoreSessions = t13;
  let t14;
  if ($[29] !== hasMoreSessions || $[30] !== loadMoreSessions || $[31] !== sessions || $[32] !== sessionsQuery || $[33] !== visibleSessions) {
    t14 = {
      sessionsQuery,
      sessions,
      visibleSessions,
      hasMoreSessions,
      loadMoreSessions,
      loading: sessionsQuery.loading,
      error: sessionsQuery.error,
      refetch: sessionsQuery.refetch
    };
    $[29] = hasMoreSessions;
    $[30] = loadMoreSessions;
    $[31] = sessions;
    $[32] = sessionsQuery;
    $[33] = visibleSessions;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}